import React, { useState, useEffect } from "react";
import FilterBar from "../components/FilterBar";
import "react-pivottable/pivottable.css";
import { useVerbatim } from "../context/VerbatimContext";
import { useSetup } from "../context/SetupContext";
import VerbatimFilter from "../components/VerbatimFilter";
import { exportTableToExcel } from "../utils/ExcelUtils";
import "./PivotPage.css";

const PivotPage = () => {
  const { getVerbatims } = useVerbatim(); // Use the getter function to access verbatims
  const [storedVerbatims, setStoredVerbatims] = useState([]);
  const { labelTypes, labels } = useSetup(); // Label types (Gender, Age, etc.)
  const [filteredVerbatims, setFilteredVerbatims] = useState([]); // Filtered verbatims to display
  const [filters, setFilters] = useState({
    source: "",
    dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
      acc[labelType] = [];
      return acc;
    }, {}),
  });
  const [sampleSizes, setSampleSizes] = useState({ rows: {}, columns: {} });

  const [displayOptions, setDisplayOptions] = useState({
    percentage: false, // Toggle for percentage display
    percentageType: "global", // 'global', 'row-wise', 'column-wise'
    calculationType: "none", // 'none', 'zscore', 'rank'
    zScoreType: "global", // 'global', 'row-wise', 'column-wise'
    rankType: "global", // 'global', 'row-wise', 'column-wise'
  });

  const [pivotData, setPivotData] = useState({});
  const [activeTab, setActiveTab] = useState("labels"); // Default to the "Settings" tab
  const [rowLabels, setRowLabels] = useState([]); // Labels for rows
  const [colLabels, setColLabels] = useState([]); // Labels for columns
  const [excludeUnknown, setExcludeUnknown] = useState(false); // New state
  const [displayMode, setDisplayMode] = useState("raw"); // Default mode is "raw"
  const [selectedRowLabel, setSelectedRowLabel] = useState("Gender"); // Default row label type
  const [selectedColLabel, setSelectedColLabel] = useState("Age"); // Default column label type
  const [selectedFirstLabel, setSelectedFirstLabel] = useState("");
  const [selectedSecondLabel, setSelectedSecondLabel] = useState("");
  const [sortBy, setSortBy] = useState({ key: 'firstColumn', order: 'asc' });
  
  // Function to handle sorting when a column header is clicked
  const selectSortBy = (columnLabel) => {
    let order = 'asc';
    if (sortBy.key === columnLabel) order = sortBy.order === 'asc' ? 'desc' : 'asc';
    setSortBy({ key: columnLabel, order });
  };

  // Fonction pour trier les lignes du tableau
  const getSortedRows = () => {
    // Check if sortBy is set and if there are row labels to sort
    if (!sortBy.key || !rowLabels.length) return rowLabels;
    // Check if the key is valid (exists in colLabels)
    const isKeyValid = colLabels.includes(sortBy.key);
    if (!isKeyValid && sortBy.key !== "firstColumn") return rowLabels;
    // Create a copy of the rowLabels array to sort
    const sortableRows = [...rowLabels];
    if (sortBy.key === "firstColumn") {
      sortableRows.sort((a, b) => {
        if (sortBy.order === 'asc') return a.localeCompare(b);
        else return b.localeCompare(a);
      });
      return sortableRows;
    }
    // Sort the rows based on the selected column and order
    sortableRows.sort((a, b) => {
      const aValue = pivotData[a]?.[sortBy.key] || 0;
      const bValue = pivotData[b]?.[sortBy.key] || 0;

      if (aValue < bValue) return sortBy.order === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortBy.order === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sortableRows;
  };

  // Separate sentiment states for rows and columns
  const [rowSentimentStates, setRowSentimentStates] = useState({
    positive: true,
    negative: true,
    mixed: true,
    neutral: true,
  });

  const [colSentimentStates, setColSentimentStates] = useState({
    positive: true,
    negative: true,
    mixed: true,
    neutral: true,
  });

  // Helper function to check if a label type is sentiment-enabled
  const isSentimentEnabled = (labelType) => {
    return labelTypes[labelType]?.sentimentEnabled || false;
  };

  // Handle row sentiment state changes
  const handleRowSentimentChange = (state) => {
    setRowSentimentStates((prev) => ({
      ...prev,
      [state]: !prev[state], // Toggle row sentiment state
    }));
  };

  // Handle column sentiment state changes
  const handleColSentimentChange = (state) => {
    setColSentimentStates((prev) => ({
      ...prev,
      [state]: !prev[state], // Toggle column sentiment state
    }));
  };

  // Assuming `selectedRowLabel`, `selectedColLabel`, and `excludeUnknown` are defined in state
  const calculateSampleSize = () => {
    return filteredVerbatims.filter((verbatim) => {
      const rowLabel =
        verbatim.labels?.[selectedRowLabel]?.[0]?.name || "Unknown";
      const colLabel =
        verbatim.labels?.[selectedColLabel]?.[0]?.name || "Unknown";

      // Apply "exclude unknown" filter if enabled
      if (
        excludeUnknown &&
        (["Unknown", "Unknown Gender", "Unknown Age"].includes(rowLabel) ||
          ["Unknown", "Unknown Gender", "Unknown Age"].includes(colLabel))
      ) {
        return false;
      }

      return true;
    }).length;
  };

  const sampleSize = calculateSampleSize();
  const isLowSampleSize = sampleSize < 30;

  // Load verbatims on component mount
  useEffect(() => {
    const verbatims = getVerbatims(); // Access verbatims from context
    setStoredVerbatims(verbatims);
  }, [getVerbatims]);

  // Update filtered verbatims when filters or verbatims change
  useEffect(() => {
    const result = VerbatimFilter(storedVerbatims, filters); // Filter the verbatims
    setFilteredVerbatims(result);
  }, [filters, storedVerbatims]);

  const generatePivotTableData = (data, rowLabelType, colLabelType) => {
    if (!Array.isArray(data) || !rowLabelType || !colLabelType) return;

    const groupedData = {};
    const rowSet = new Set();
    const colSet = new Set();
    const localSampleSizes = { rows: {}, columns: {}, global: new Set() }; // Local sample size object

    // Helper function: Check if a label's sentiment matches the active sentiment states
    const isSentimentMatching = (sentimentStates, label) => {
      return label.state && sentimentStates[label.state.toLowerCase()];
    };

    // Helper function: Calculate percentages
    const calculatePercentagesWithSample = (data, sampleSizes, type) => {
      if (type === "row-wise") {
        Object.keys(data).forEach((row) => {
          const sampleSize = localSampleSizes.rows[row] || 1; // Use row sample size
          Object.keys(data[row]).forEach((col) => {
            const value = data[row][col];
            data[row][col] = ((isNaN(value) ? 0 : value) / sampleSize) * 100;
          });
        });
      } else if (type === "column-wise") {
        Object.keys(data).forEach((row) => {
          Object.keys(data[row]).forEach((col) => {
            const sampleSize = localSampleSizes.columns[col] || 1; // Use column sample size
            const value = data[row][col];
            data[row][col] = ((isNaN(value) ? 0 : value) / sampleSize) * 100;
          });
        });
      } else if (type === "global") {
        const globalSampleSize = localSampleSizes.global.size || 1; // Use global sample size
        Object.keys(data).forEach((row) => {
          Object.keys(data[row]).forEach((col) => {
            const value = data[row][col];
            data[row][col] = ((isNaN(value) ? 0 : value) / globalSampleSize) * 100;
          });
        });
      }
    };

    // Helper function: Calculate Z-Scores
    const calculateZScores = (data, rowSet, colSet, type) => {
      // Helper function to calculate Z-scores
      const calculate = (values) => {
        if (values.length === 0) return [];

        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        const variance =
          values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
        const stdDev = Math.sqrt(variance);

        const epsilon = 1e-6; // Threshold to avoid division by zero or small stdDev

        return values.map((val) => {
          const divisor = stdDev < epsilon ? epsilon : stdDev;
          return parseFloat(((val - mean) / divisor).toFixed(2));
        });
      };

      // **Global Z-Score Calculation**
      if (type === "global") {
        let allValues = Object.values(data)
          .flatMap((row) => Object.values(row))
          .filter((val) => val !== undefined);

        // Adjust for percentages by scaling back if necessary
        allValues = allValues.map((val) => val * 100); // Rescale percentages

        const zScores = calculate(allValues);
        let index = 0;
        Object.keys(data).forEach((row) => {
          Object.keys(data[row]).forEach((col) => {
            data[row][col] = zScores[index++];
          });
        });
      }

      // **Row-Wise Z-Score Calculation**
      else if (type === "row-wise") {
        Object.keys(data).forEach((row) => {
          let values = Object.values(data[row]);

          // Adjust for percentages by scaling up
          values = values.map((val) => val * 100); // Rescale percentages

          const zScores = calculate(values);
          Object.keys(data[row]).forEach((col, index) => {
            data[row][col] = zScores[index];
          });
        });
      }

      // **Column-Wise Z-Score Calculation**
      else if (type === "column-wise") {
        colSet.forEach((col) => {
          let values = Array.from(rowSet).map((row) => data[row]?.[col] || 0);

          // Adjust for percentages by scaling up
          values = values.map((val) => val * 100); // Rescale percentages

          const zScores = calculate(values);
          Array.from(rowSet).forEach((row, index) => {
            data[row][col] = zScores[index];
          });
        });
      }
    };


    // Helper function: Calculate Rankings
    const calculateRankings = (data, rowSet, colSet, type) => {
      if (type === "global") {
        const allValues = Object.values(data)
          .flatMap((row) => Object.values(row))
          .sort((a, b) => b - a);

        Object.keys(data).forEach((row) => {
          Object.keys(data[row]).forEach((col) => {
            data[row][col] = allValues.indexOf(data[row][col]) + 1;
          });
        });
      } else if (type === "row-wise") {
        Object.keys(data).forEach((row) => {
          const values = Object.values(data[row]).sort((a, b) => b - a);
          Object.keys(data[row]).forEach((col) => {
            data[row][col] = values.indexOf(data[row][col]) + 1;
          });
        });
      } else if (type === "column-wise") {
        colSet.forEach((col) => {
          const values = Array.from(rowSet)
            .map((row) => data[row]?.[col] || 0)
            .sort((a, b) => b - a);
          Array.from(rowSet).forEach((row) => {
            data[row][col] = values.indexOf(data[row][col]) + 1;
          });
        });
      }
    };

    // Step 1: Group and count occurrences
    data.forEach((verbatim) => {
      const rowLabels = (verbatim.labels?.[rowLabelType] || [])
        .filter(
          (label) =>
            label &&
            label.name &&
            (!isSentimentEnabled(rowLabelType) || isSentimentMatching(rowSentimentStates, label))
        )
        .map((label) => label.name);

      const colLabels = (verbatim.labels?.[colLabelType] || [])
        .filter(
          (label) =>
            label &&
            label.name &&
            (!isSentimentEnabled(colLabelType) || isSentimentMatching(colSentimentStates, label))
        )
        .map((label) => label.name);

      let finalRowLabels = rowLabels.length > 0 ? rowLabels : ["Unknown"];
      let finalColLabels = colLabels.length > 0 ? colLabels : ["Unknown"];

      if (excludeUnknown) {
        const validRowLabels = finalRowLabels.filter(
          (label) => !["Unknown", "Unknown Gender", "Unknown Age"].includes(label)
        );
        const validColLabels = finalColLabels.filter(
          (label) => !["Unknown", "Unknown Gender", "Unknown Age"].includes(label)
        );

        if (validRowLabels.length === 0 || validColLabels.length === 0) {
          return; // Skip this verbatim if it doesn't have valid labels
        }

        finalRowLabels = validRowLabels;
        finalColLabels = validColLabels;
      }

      // Add labels to row and column sets
      finalRowLabels.forEach((label) => rowSet.add(label));
      finalColLabels.forEach((label) => colSet.add(label));

      // Group data and track unique verbatims
      finalRowLabels.forEach((rowLabel) => {
        finalColLabels.forEach((colLabel) => {
          if (!groupedData[rowLabel]) groupedData[rowLabel] = {};
          if (!groupedData[rowLabel][colLabel]) groupedData[rowLabel][colLabel] = 0;

          groupedData[rowLabel][colLabel] += 1;

          // Track unique verbatims for sample size calculation
          if (!localSampleSizes.rows[rowLabel]) localSampleSizes.rows[rowLabel] = new Set();
          if (!localSampleSizes.columns[colLabel]) localSampleSizes.columns[colLabel] = new Set();

          localSampleSizes.rows[rowLabel].add(verbatim.id);
          localSampleSizes.columns[colLabel].add(verbatim.id);
          localSampleSizes.global.add(verbatim.id); // Add to global sample size
        });
      });
    });

    // Convert sets to counts for sample sizes
    Object.keys(localSampleSizes.rows).forEach(
      (row) => (localSampleSizes.rows[row] = localSampleSizes.rows[row].size)
    );
    Object.keys(localSampleSizes.columns).forEach(
      (col) => (localSampleSizes.columns[col] = localSampleSizes.columns[col].size)
    );

    // Update state with the new sample sizes
    setSampleSizes({
      rows: localSampleSizes.rows,
      columns: localSampleSizes.columns,
    });

    // Step 2: Apply Percentage Calculation if Enabled
    if (displayOptions.percentage) {
      calculatePercentagesWithSample(groupedData, sampleSizes, displayOptions.percentageType);
    }

    // Step 3: Apply Additional Calculations (Z-Score or Ranking) if Specified
    if (displayOptions.calculationType === "zscore") {
      calculateZScores(groupedData, rowSet, colSet, displayOptions.zScoreType);
    } else if (displayOptions.calculationType === "rank") {
      calculateRankings(groupedData, rowSet, colSet, displayOptions.rankType);
    }

    console.log(groupedData)

    // Finalize and Set Outputs
    setPivotData(groupedData);
    setRowLabels(Array.from(rowSet));
    setColLabels(Array.from(colSet));
  };


  useEffect(() => {
    generatePivotTableData(
      filteredVerbatims,
      selectedRowLabel,
      selectedColLabel
    );
  }, [
    filteredVerbatims,
    selectedRowLabel,
    selectedColLabel,
    rowSentimentStates,
    colSentimentStates,
    excludeUnknown,
    displayOptions, // Ensure recalculation when displayOptions changes
  ]);

  // Function to get sorted rows based on the selected column and order
  const sortedRows = getSortedRows();

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Function to calculate background color for heatmap based on value, with a light yellow to dark green gradient
  // Heatmap color function for Z-scores: yellow-green for positive, gray for negative
  const getHeatmapColor = (value) => {
    if (displayOptions.calculationType === "zscore") {
      // Z-Score Display: Differentiated by positive/negative
      if (value >= 0) {
        const intensity = Math.min(value / 3, 1); // Cap at Z-score of 3
        return `rgba(0, 123, 255, ${0.3 + 0.7 * intensity})`; // Darker blue for positive Z-scores
      } else {
        const intensity = Math.min(Math.abs(value) / 3, 1); // Cap at Z-score of -3
        return `rgba(173, 216, 230, ${0.3 + 0.7 * intensity})`; // Lighter blue for negative Z-scores
      }
    } else if (displayOptions.calculationType === "rank") {
      // Rank Display: Scaling intensity by rank
      if (value === 0) {
        return `rgba(255, 255, 255, 1)`; // Pure white for rank 0
      }

      const maxRank = 10; // Adjust this if rank range varies
      const intensity = Math.min(1 - (value - 1) / maxRank, 1); // Inverse intensity for rank
      return `rgba(0, 123, 255, ${0.3 + 0.7 * intensity})`; // Blue gradient for ranks
    } else if (displayOptions.percentage) {
      // Percentage Display: Light to dark blue gradient
      const intensity = Math.min(value / 100, 1); // Cap percentage intensity at 100%
      return `rgba(0, 123, 255, ${0.3 + 0.7 * intensity})`; // Blue gradient for percentage
    } else {
      // Default Display (Raw Values): Light to dark gradient
      const maxRawValue = Math.max(...Object.values(pivotData).flatMap(row => Object.values(row))); // Determine max raw value dynamically
      const intensity = Math.min(value / maxRawValue, 1); // Scale intensity for raw values
      return `rgba(0, 123, 255, ${0.3 + 0.7 * intensity})`; // Unified blue gradient for raw values
    }
  };

  return (
    <div className="pivot-page">
      <div className="filter-bar-section">
        <FilterBar onFilterChange={handleFilterChange} />
      </div>

      <div className="pivot-section">
        <div className="tab-buttons">
          <button
            className={activeTab === "labels" ? "active" : ""}
            onClick={() => setActiveTab("labels")}
          >
            Labels
          </button>
          <button
            className={activeTab === "settings" ? "active" : ""}
            onClick={() => setActiveTab("settings")}
          >
            Settings
          </button>
        </div>

        <div className="pivot-controls">
          {activeTab === "settings" && (
            <div className="settings-tab">
              {/* Toggle for Exclude Unknown */}
              <div className="toggle-container">
                <div
                  className={`toggle-switch ${excludeUnknown ? "active" : ""}`}
                  onClick={() => setExcludeUnknown(!excludeUnknown)}
                >
                  <div className="toggle-thumb" />
                </div>
                <label>Exclude "Unknown" entries</label>
              </div>
              <div className="settings-row">
                {/* Percentage Toggle */}
                <div className="toggle-container">
                  <div
                    className={`toggle-switch ${displayOptions.percentage ? "active" : ""}`}
                    onClick={() =>
                      setDisplayOptions((prev) => ({
                        ...prev,
                        percentage: !prev.percentage,
                      }))
                    }
                  >
                    <div className="toggle-thumb" />
                  </div>
                  <label>Apply Percentage Calculation:</label>
                </div>
                <select
                  value={displayOptions.percentageType}
                  onChange={(e) =>
                    setDisplayOptions((prev) => ({
                      ...prev,
                      percentageType: e.target.value,
                    }))
                  }
                  disabled={!displayOptions.percentage} // Disable if percentage toggle is off
                  className={!displayOptions.percentage ? "disabled" : ""}
                >
                  <option value="global">Global</option>
                  <option value="row-wise">Row-wise</option>
                  <option value="column-wise">Column-wise</option>
                </select>

                {/* Calculation Type Dropdown */}
                <label>
                  Apply Additional Calculation:
                  <select
                    value={displayOptions.calculationType}
                    onChange={(e) =>
                      setDisplayOptions((prev) => ({
                        ...prev,
                        calculationType: e.target.value,
                      }))
                    }
                  >
                    <option value="none">None</option>
                    <option value="zscore">Z-score</option>
                    <option value="rank">Ranking</option>
                  </select>
                </label>

                {/* Z-Score Type Dropdown */}
                {displayOptions.calculationType === "zscore" && (
                  <label>
                    Z-Score Type:
                    <select
                      value={displayOptions.zScoreType}
                      onChange={(e) =>
                        setDisplayOptions((prev) => ({
                          ...prev,
                          zScoreType: e.target.value,
                        }))
                      }
                    >
                      <option value="global">Global</option>
                      <option value="row-wise">Row-wise</option>
                      <option value="column-wise">Column-wise</option>
                    </select>
                  </label>
                )}

                {/* Rank Type Dropdown */}
                {displayOptions.calculationType === "rank" && (
                  <label>
                    Rank Type:
                    <select
                      value={displayOptions.rankType}
                      onChange={(e) =>
                        setDisplayOptions((prev) => ({
                          ...prev,
                          rankType: e.target.value,
                        }))
                      }
                    >
                      <option value="global">Global</option>
                      <option value="row-wise">Row-wise</option>
                      <option value="column-wise">Column-wise</option>
                    </select>
                  </label>
                )}
              </div>
            </div>
          )}

          {activeTab === "labels" && (
            <div className="labels-tab">
              {/* Label Selection Row */}
              <div className="pivot-label-selection">
                <div className="pivot-label-select">
                  <label>
                    Labels group to display in the rows:
                    <select
                      value={selectedRowLabel}
                      onChange={(e) => setSelectedRowLabel(e.target.value)}
                    >
                      {Object.keys(labelTypes).map((labelType) => (
                        <option key={labelType} value={labelType}>
                          {labelType}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="pivot-label-select">
                  <label>
                    Labels group to display in the columns:
                    <select
                      value={selectedColLabel}
                      onChange={(e) => { 
                        setSelectedColLabel(e.target.value); 
                        setSortBy({ key: 'firstColumn', order: 'asc' })
                      }}
                    >
                      {Object.keys(labelTypes).map((labelType) => (
                        <option key={labelType} value={labelType}>
                          {labelType}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              <div className="pivot-label-selection">
                <h2 style={{fontSize: '14px', color: '#333', fontWeight: 500}}>Cross-analysis of two attributes without summing values: </h2>
                  <label>
                    <select value={selectedFirstLabel} onChange={(e) => setSelectedFirstLabel(e.target.value)}>
                      {labels?.[selectedRowLabel]?.map((label) => (
                        <option key={label.name} value={label.name}>
                          {label.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <select value={selectedSecondLabel} onChange={(e) => setSelectedSecondLabel(e.target.value)}>
                      {labels?.[selectedColLabel]?.map((label) => (
                        <option key={label.name} value={label.name}>
                          {label.name}
                        </option>
                      ))}
                    </select>
                  </label>
              </div>
              {/* Sentiment Controls Row */}
              <div className="sentiment-controls-row">
                {/* Sentiment Filter Controls for Row Label */}
                {isSentimentEnabled(selectedRowLabel) && (
                  <div className="sentiment-controls">
                    <p>Filter sentiment for {selectedRowLabel}:</p>

                    <label>
                      <input
                        type="checkbox"
                        checked={rowSentimentStates.positive}
                        onChange={() => handleRowSentimentChange("positive")}
                      />
                      Positive
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        checked={rowSentimentStates.negative}
                        onChange={() => handleRowSentimentChange("negative")}
                      />
                      Negative
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        checked={rowSentimentStates.mixed}
                        onChange={() => handleRowSentimentChange("mixed")}
                      />
                      Mixed
                    </label>
                  </div>
                )}

                {/* Sentiment Filter Controls for Column Label */}
                {isSentimentEnabled(selectedColLabel) && (
                  <div className="sentiment-controls">
                    <p>Filter {selectedColLabel} by sentiment:</p>

                    <label>
                      <input
                        type="checkbox"
                        checked={colSentimentStates.positive}
                        onChange={() => handleColSentimentChange("positive")}
                      />
                      Positive
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        checked={colSentimentStates.negative}
                        onChange={() => handleColSentimentChange("negative")}
                      />
                      Negative
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        checked={colSentimentStates.mixed}
                        onChange={() => handleColSentimentChange("mixed")}
                      />
                      Mixed
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
        <div className="pivot-sample-container">
          <div className={`sample-size ${isLowSampleSize ? "low" : ""}`}>
            {isLowSampleSize
              ? `Sample size is low (${sampleSize} conversations). Interpret data with caution.`
              : `Sample size: ${sampleSize} conversations`}
          </div>
          <button
            className="icon-button export-button"
            onClick={() =>
              exportTableToExcel(
                pivotData,
                rowLabels,
                colLabels,
                selectedRowLabel,
                selectedColLabel,
                "PivotTable.xlsx",
                ["percentage", "row_percentage", "column_percentage"].includes(displayMode), // Determine if data is percentage-based
                sampleSize, // Pass the sample size
                displayMode // Pass the current display mode
              )
            }
            title="Export Data"
          >
            <i className="fa-solid fa-download"></i>
          </button>
        </div>
        <div className="pivot-table-section">
          {filteredVerbatims.length > 0 && (
            <table className="pivot-table">
              <thead>
                <tr>
                  <th
                    onClick={() => selectSortBy('firstColumn')}
                    style={{ cursor: 'pointer' }}
                  >
                    {selectedRowLabel} / {selectedColLabel}
                    {sortBy.key === 'firstColumn' && (
                      <i 
                        className={sortBy.order === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'}
                        style={{ marginLeft: '5px' }}
                      ></i>
                    )}
                  </th>
                  {colLabels.map((colLabel, index) => (
                    <th 
                      key={index} 
                      title={colLabel}
                      onClick={() => selectSortBy(colLabel)}
                      style={{ cursor: 'pointer' }}
                    >
                      {colLabel}
                      {sortBy.key === colLabel && (
                        <i 
                          className={sortBy.order === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'}
                          style={{ marginLeft: '5px' }}
                        ></i>
                      )}
                    </th>
                  ))}
                  {/* Add a header for row-wise sample sizes */}
                  {displayOptions.percentage && displayOptions.percentageType === "row-wise" && (
                    <th style={{ fontWeight: "bold" }}>Sample</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length > 0 && (
                  sortedRows.map((rowLabel, rowIndex) => (
                    <tr key={rowIndex}>
                      <td title={rowLabel}>{rowLabel}</td>
                      {colLabels.map((colLabel, colIndex) => {
                        const value = pivotData[rowLabel]?.[colLabel] || 0;
                        let displayValue;

                        if (
                          displayOptions.calculationType === "zscore" &&
                          ["global", "row-wise", "column-wise"].includes(displayOptions.zScoreType)
                        ) {
                          displayValue = value.toFixed(2);
                        } else if (
                          displayOptions.calculationType === "rank" &&
                          ["global", "row-wise", "column-wise"].includes(displayOptions.rankType)
                        ) {
                          displayValue = value;
                        } else if (
                          displayOptions.percentage &&
                          ["global", "row-wise", "column-wise"].includes(displayOptions.percentageType)
                        ) {
                          displayValue = `${value.toFixed(1)}%`;
                        } else {
                          displayValue = value;
                        }

                        return (
                          <td
                            key={colIndex}
                            style={{
                              backgroundColor: getHeatmapColor(value),
                              color: "#000000",
                            }}
                          >
                            {displayValue}
                          </td>
                        );
                      })}
                      {/* Conditionally display the sample size column for rows */}
                      {displayOptions.percentage &&
                        displayOptions.percentageType === "row-wise" && (
                          <td style={{ fontWeight: "bold" }}>{sampleSizes.rows[rowLabel] || 0}</td>
                        )}
                    </tr>
                  ))
                )}
                {/* Conditionally display the sample size row for columns */}
                {displayOptions.percentage && displayOptions.percentageType === "column-wise" && (
                  <tr>
                    <td style={{ fontWeight: "bold" }}>Sample</td>
                    {colLabels.map((colLabel, colIndex) => (
                      <td key={colIndex} style={{ fontWeight: "bold", textAlign: "center" }}>
                        {sampleSizes.columns[colLabel] || 0}
                      </td>
                    ))}
                    {/* Remove the extra <td> */}
                  </tr>
                )}

              </tbody>
            </table>
          )}
        </div>
      </div>
    </div >
  );
};

export default PivotPage;
